
/* 
 * FadeToColorTransition.js
 * Created on 15-06-2021
 * @author Henrique Campos for &ranj
 * 
 * This transition creates a createjs.Shape that fills the whole screen with a given color. This shape then
 * fades in, enables/disables the screen, and then fades out.
 * 
 * This transition requires FadeToColorOptions to be given as the animation options;
 */

import ScreenNavigator from "../../ScreenNavigator";
import AnimationFactory from "../AnimationFactory";
import FadeToColorOptions from "./FadeToColorOptions";

export default class FadeToColorTransition {
    constructor(type) {
        this.type = type;
    };

    /**
     * 
     * @param {ScreenNavigator} screenNavigator 
     * @param {createjs.DisplayObject} screen 
     * @param {FadeToColorOptions} animationOptions 
     * @returns {Promise}
     */
    animate(screenNavigator, screen, animationOptions = new FadeToColorOptions()) {
        return new Promise(function (resolve, reject) {
            // create shape that covers screen with given color
            let fadeShape = this._createColorShape(screenNavigator, animationOptions.color);
            // ensure that screen to be shown is not visible
            let isShowing = this.type === AnimationFactory.FADE_TO_COLOR_IN;
            if(isShowing){
                screen.visible = false;
            }
            // Execute the tween with the given animation options
            fadeShape.alpha = 0;
            createjs.Tween
                .get(fadeShape)
                .wait(animationOptions.delay)
                .to({ alpha: 1 }, animationOptions.duration / 2, animationOptions.ease)
                .wait(animationOptions.colorWait)
                .call(function() {
                    // set screen to showing state
                    screen.visible = isShowing;
                })
                .to({ alpha: 0 }, animationOptions.duration / 2, animationOptions.ease)
                .call(function () {
                    this._destroyColorShape(screenNavigator, fadeShape);
                    // handle callbacks in animation options
                    if (animationOptions.callbacks)
                        for (let i = 0; i < animationOptions.callbacks.length; i++) {
                            animationOptions.callbacks[i].call(this);
                        }
                    resolve();
                }.bind(this));
        }.bind(this));
    };

    _createColorShape(screenNavigator, color) {
        const fadeShape = new createjs.Shape(new createjs.Graphics()
            .beginFill(color)
            .drawRect(0, 0, screenNavigator.screenWidth, screenNavigator.screenHeight));
        // adds shape to the context view so it is drawn on top of any screen/pop-up and
        // avoids unexpected behaviors caused by the ScreenNavigator screen handling.
        screenNavigator.contextView.addChild(fadeShape);
        // adds interaction consumer to avoid interactions with elements beneath the fade shape
        let consumeInteraction = function(){};
        fadeShape.addEventListener('click', consumeInteraction);
        fadeShape.addEventListener('mousedown', consumeInteraction);
        fadeShape.addEventListener('dblclick', consumeInteraction);
        fadeShape.addEventListener('pressmove', consumeInteraction);
        fadeShape.addEventListener('pressup', consumeInteraction);
        fadeShape.addEventListener('mouseover', consumeInteraction);
        fadeShape.addEventListener('mouseout', consumeInteraction);
        fadeShape.addEventListener('rollover', consumeInteraction);
        fadeShape.addEventListener('rollout', consumeInteraction);
        return fadeShape;
    }

    _destroyColorShape(screenNavigator, fadeShape){
        fadeShape.removeAllEventListeners();
        screenNavigator.contextView.removeChild(fadeShape);
    }
}